import { useEffect, useRef, useState } from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import resetPasswordIcon from "../../../../assets/buttons/reset_password.svg";
import setPasswordIcon from "../../../../assets/buttons/set_password_icon.svg";

import {
  Button,
  FormWarning,
  PasswordStrength,
} from "@shltelemedicine/shl-ui-lib";
import { passwordPattern } from "../../../../constants";
import useAlert from "../../../../hooks/useAlert";
import { UserStatus, UserType } from "../../../../models/users.interface";
import usersService from "../../../../services/users.service";
import AlertModal from "../../../Shared/AlertModal/AlertModal";
import CheckBoxFormInput from "../../../Shared/CheckBox/CheckBoxFormInput";
import TextFormInput from "../../../Shared/TextInput/TextFormInput";
import PasswordRules from "../../../ChangePassword/PasswordRules/PasswordRules";

enum SecurityFormKeys {
  password = "password",
  passwordConfirmation = "passwordConfirmation",
  isOneTimePassword = "isOneTimePassword",
}
type SecurityFormInputs = {
  password: string;
  passwordConfirmation: string;
  isOneTimePassword: boolean;
};

type SecurityDetailsProps = {
  userId: string;
  userEmail: string;
  userStatus: UserStatus;
  userType: UserType;
  onUpdated?: () => void;
};

const SecurityDetails = ({
  userId,
  userEmail,
  userStatus,
  onUpdated,
}: SecurityDetailsProps) => {
  const intl = useIntl();

  const isPendingUser = userStatus === "pending";
  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    control,
    setError,
    formState: {
      errors: formErrors,
      isDirty,
      dirtyFields: { passwordConfirmation: isPasswordConfirmationDirty },
    },
    reset,
    watch,
  } = useForm<SecurityFormInputs>({
    defaultValues: { isOneTimePassword: true },
  });

  useEffect(() => {
    reset();
  }, [userEmail, reset]);

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const isOneTimePassword = watch("isOneTimePassword") ?? false;

  const passwordRef = useRef<string>("");
  passwordRef.current = watch(SecurityFormKeys.password);

  const saveHandler = async ({ password }: SecurityFormInputs) => {
    setLoading(true);
    try {
      const { success, errorCode } = await usersService.resetPassword(
        userId,
        password,
        isOneTimePassword
      );
      if (!success) {
        if (Number(errorCode) === 400) {
          setError("passwordConfirmation", {
            type: "pattern",
            message: intl.messages[
              "SETTINGS.VALID_PASSWORD_REQUIRED"
            ] as string,
          });
          return;
        }
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.RESET_PASSWORD",
          "An error occurred while trying to reset user's password.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      reset();

      if (onUpdated) onUpdated();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Form
        className="d-flex flex-column bg-white p-4"
        onSubmit={handleSubmit(saveHandler)}
      >
        <Form.Group as={Row} className="align-items-center mb-2">
          <Form.Label column>
            <FormattedMessage id="USER.EMAIL" defaultMessage="Email" />
          </Form.Label>
          <Col sm={7}>{userEmail}</Col>
        </Form.Group>
        <Form.Group as={Row} className="align-items-center mb-2">
          <Form.Label column>
            <FormattedMessage id="LOGIN.PASSWORD" defaultMessage="Password" />
          </Form.Label>
          <Col sm={7}>
            <TextFormInput
              name={"password"}
              control={control}
              rules={{
                required: true,
                minLength: 8,
                pattern: passwordPattern,
              }}
              input={{
                type: "password",
                isInvalid: !!formErrors?.password,
                disabled: loading,
              }}
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="align-items-center mb-2">
          <Form.Label column>
            <FormattedMessage
              id="CHANGE_PASSWORD.RETYPE_PASSWORD"
              defaultMessage="Confirm Password"
            />
          </Form.Label>
          <Col sm={7}>
            <TextFormInput
              name={"passwordConfirmation"}
              control={control}
              rules={{
                required: true,
                validate: {
                  passwordMismatch: (value) => value === passwordRef.current,
                },
              }}
              input={{
                type: "password",
                isInvalid: !!formErrors?.passwordConfirmation,
                disabled: loading,
              }}
            />
          </Col>
        </Form.Group>
        {(!!formErrors?.password || !!formErrors?.passwordConfirmation) && (
          <FormWarning
            className="font-sm flex-grow-1"
            message={
              <FormattedMessage
                id="SETTINGS.VALID_PASSWORD_REQUIRED"
                defaultMessage="Insert Valid password"
              />
            }
          />
        )}
        <Form.Group as={Row} className="align-items-center mb-2 text-nowrap ">
          <Col sm={7}>
            <CheckBoxFormInput
              name={"isOneTimePassword"}
              control={control}
              input={{
                label: (
                  <FormattedMessage
                    id="CHANGE_PASSWORD.ENABLE_ONE_TIME_PASSWORD"
                    defaultMessage="Enable one time password"
                  />
                ),
              }}
            />
          </Col>
        </Form.Group>

        <PasswordStrength password={passwordRef.current} className="mb-3" />

        <Button
          type="submit"
          icon={{
            src: isPendingUser ? setPasswordIcon : resetPasswordIcon,
            width: 22,
            height: 22,
          }}
          bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
          className="align-self-end capitalized"
          disabled={loading || !isDirty || !isPasswordConfirmationDirty}
        >
          <FormattedMessage
            id={
              isPendingUser
                ? "SECURITY.SET_PASSWORD"
                : "SECURITY.RESET_PASSWORD"
            }
            defaultMessage={isPendingUser ? "Set Password" : "Reset Password"}
          />
          {loading && (
            <>
              &nbsp;&nbsp;
              <Spinner as="span" animation="border" size="sm" />
            </>
          )}
        </Button>
      </Form>
      <PasswordRules />
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </>
  );
};
export default SecurityDetails;
